/* eslint-disable max-len */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { asRem } from 'lib/css';
import { Pages } from 'core/pages';
import { friendlyDate, parseDate } from 'lib/utils';
import {
  CalendarEventListQuery,
  CelebrationQuery,
} from './leave/query';

const PreviewWrapper = styled.div`
  padding: ${asRem(32)} ${asRem(40)} ${asRem(100)};
  max-width: ${asRem(960)};
  margin: 0 auto;
  font-family: 'DM Sans', -apple-system, sans-serif;

  .greeting {
    margin-bottom: ${asRem(28)};

    .greeting-name {
      font-family: 'DM Serif Display', serif;
      font-size: ${asRem(36)};
      color: #f0eff4;
      font-weight: 400;
      line-height: 1.2;
      margin-bottom: ${asRem(6)};
    }

    .greeting-sub {
      font-size: ${asRem(14)};
      color: #6b697a;
    }
  }

  .leave-balance-card {
    padding: 0 0 ${asRem(28)};
    margin-bottom: ${asRem(28)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${asRem(16)};
    border-bottom: 1px solid #2a2a32;

    .leave-balance-left {
      display: flex;
      flex-direction: column;
      gap: ${asRem(16)};
    }

    .leave-balance-label {
      font-size: ${asRem(11)};
      text-transform: uppercase;
      letter-spacing: 1.2px;
      color: #7c7a8a;
      font-weight: 500;
      margin-bottom: ${asRem(4)};
    }

    .leave-balance-row {
      display: flex;
      align-items: baseline;
      gap: ${asRem(8)};
    }

    .leave-balance-number {
      font-family: 'DM Serif Display', serif;
      font-size: ${asRem(52)};
      color: #f0eff4;
      line-height: 1;
      font-weight: 400;
    }

    .leave-balance-sub {
      font-size: ${asRem(15)};
      color: #9d9ba6;
    }

    .request-leave-btn {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(8)};
      padding: ${asRem(10)} ${asRem(20)};
      background: #9b7aef;
      color: #fff;
      border: none;
      border-radius: ${asRem(10)};
      font-size: ${asRem(13)};
      font-weight: 600;
      text-decoration: none;
      font-family: inherit;

      svg {
        width: ${asRem(16)};
        height: ${asRem(16)};
      }
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${asRem(16)};

    .section-title {
      font-size: ${asRem(11)};
      text-transform: uppercase;
      letter-spacing: 1.2px;
      color: #7c7a8a;
      font-weight: 500;
    }

    .section-action {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(4)};
      font-size: ${asRem(12)};
      color: #9b7aef;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      padding: ${asRem(4)} ${asRem(10)};
      border-radius: ${asRem(6)};
      transition: background 0.2s, color 0.2s;

      &:hover {
        background: rgba(155, 122, 239, 0.12);
        color: #b49af5;
      }

      svg {
        width: ${asRem(14)};
        height: ${asRem(14)};
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(${asRem(2)});
      }
    }
  }

  .leaves-section {
    margin-bottom: ${asRem(36)};
    padding-bottom: ${asRem(36)};
    border-bottom: 1px solid #1e1e24;
  }

  .empty-state {
    padding: ${asRem(24)} ${asRem(32)};
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${asRem(16)};

    .empty-illustration {
      width: ${asRem(80)};
      height: ${asRem(80)};

      svg {
        width: 100%;
        height: 100%;
      }
    }

    .empty-text {
      .empty-title {
        font-size: ${asRem(16)};
        font-weight: 600;
        color: #f0eff4;
        margin-bottom: ${asRem(4)};
      }

      .empty-desc {
        font-size: ${asRem(14)};
        color: #6b697a;
      }
    }
  }

  .scroll-section {
    position: relative;

    &:hover .scroll-arrow {
      opacity: 1;
    }

  }

  .scroll-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(12, 12, 14, 0.6);
    border: none;
    color: #6b697a;
    cursor: pointer;
    z-index: 3;
    opacity: 0;
    transition: opacity 0.25s, color 0.2s, background 0.2s;
    padding: ${asRem(8)};
    border-radius: ${asRem(8)};

    &:hover {
      color: #f0eff4;
      background: rgba(12, 12, 14, 0.85);
    }

    &.left {
      left: ${asRem(4)};
    }

    &.right {
      right: ${asRem(4)};
    }

    &.hidden {
      pointer-events: none;
      opacity: 0 !important;
    }

    svg {
      width: ${asRem(20)};
      height: ${asRem(20)};
      display: block;
    }
  }

  .scroll-track {
    display: flex;
    gap: ${asRem(12)};
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: ${asRem(12)} ${asRem(4)};

    &::-webkit-scrollbar {
      display: none;
    }

    &.fade-right {
      -webkit-mask-image: linear-gradient(to right, black calc(100% - ${asRem(60)}), transparent);
      mask-image: linear-gradient(to right, black calc(100% - ${asRem(60)}), transparent);
    }

    &.fade-left {
      -webkit-mask-image: linear-gradient(to left, black calc(100% - ${asRem(60)}), transparent);
      mask-image: linear-gradient(to left, black calc(100% - ${asRem(60)}), transparent);
    }

    &.fade-both {
      -webkit-mask-image: linear-gradient(to right, transparent, black ${asRem(60)}, black calc(100% - ${asRem(60)}), transparent);
      mask-image: linear-gradient(to right, transparent, black ${asRem(60)}, black calc(100% - ${asRem(60)}), transparent);
    }
  }

  .holidays-section {
    margin-bottom: ${asRem(36)};
  }

  .holiday-card {
    background: #151518;
    border: 1px solid #2a2a32;
    border-radius: ${asRem(14)};
    padding: ${asRem(18)} ${asRem(20)};
    min-width: ${asRem(180)};
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    transition: background 0.25s ease, border-color 0.25s ease;
    cursor: pointer;

    &:hover {
      background: #1a1a1f;
      border-color: #3a3a44;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: #9b7aef;
      transition: height 0.25s ease;
    }

    &:hover::before {
      height: 5px;
    }

    &.optional::before {
      background: #2dd4bf;
    }

    .holiday-date-line {
      font-size: ${asRem(14)};
      font-weight: 600;
      color: #9b7aef;
      margin-bottom: ${asRem(12)};
    }

    &.optional .holiday-date-line {
      color: #2dd4bf;
    }

    .holiday-name {
      font-size: ${asRem(15)};
      color: #f0eff4;
      font-weight: 600;
      margin-bottom: ${asRem(6)};
    }

    .holiday-tag {
      font-size: ${asRem(10)};
      color: #6b697a;
      text-transform: uppercase;
      letter-spacing: 0.8px;
    }
  }

  .celebrations-section {
    margin-bottom: ${asRem(32)};
  }

  .celeb-card {
    background: #18191f;
    border-radius: ${asRem(14)};
    border: 1px solid rgba(45, 212, 191, 0.3);
    padding: ${asRem(24)} ${asRem(16)} ${asRem(20)};
    text-align: center;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: ${asRem(166)};
    flex-shrink: 0;
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    cursor: pointer;
    box-shadow:
      0 0 ${asRem(8)} rgba(45, 212, 191, 0.12),
      0 0 ${asRem(20)} rgba(45, 212, 191, 0.06);

    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: #2dd4bf;
      border-radius: 0 0 3px 3px;
    }

    &:hover {
      transform: translateY(${asRem(-4)}) scale(1.03);
      border-color: rgba(45, 212, 191, 0.7);
      box-shadow:
        0 0 ${asRem(14)} rgba(45, 212, 191, 0.25),
        0 0 ${asRem(28)} rgba(45, 212, 191, 0.12),
        0 ${asRem(8)} ${asRem(24)} rgba(45, 212, 191, 0.12);
    }

    &.birthday {
      border-color: rgba(155, 122, 239, 0.3);
      box-shadow:
        0 0 ${asRem(8)} rgba(155, 122, 239, 0.12),
        0 0 ${asRem(20)} rgba(155, 122, 239, 0.06);
    }

    &.birthday::before {
      background: #9b7aef;
    }

    &.birthday:hover {
      border-color: rgba(155, 122, 239, 0.7);
      box-shadow:
        0 0 ${asRem(14)} rgba(155, 122, 239, 0.25),
        0 0 ${asRem(28)} rgba(155, 122, 239, 0.12),
        0 ${asRem(8)} ${asRem(24)} rgba(155, 122, 239, 0.12);
    }

    .celeb-icon {
      width: ${asRem(56)};
      height: ${asRem(56)};
      border-radius: 50%;
      margin: 0 auto ${asRem(12)};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${asRem(28)};
      position: relative;
      z-index: 1;

      &.teal { background: rgba(45, 212, 191, 0.12); }
      &.purple { background: rgba(155, 122, 239, 0.12); }
    }

    .celeb-name {
      font-size: ${asRem(14)};
      font-weight: 600;
      color: #f0eff4;
      margin-bottom: ${asRem(2)};
      position: relative;
      z-index: 1;
    }

    .celeb-date {
      font-size: ${asRem(12)};
      color: #6b697a;
      margin-bottom: ${asRem(10)};
      position: relative;
      z-index: 1;
    }

    .celeb-badge {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(3)};
      font-size: ${asRem(11)};
      font-weight: 600;
      padding: ${asRem(3)} ${asRem(8)};
      border-radius: ${asRem(5)};
      position: relative;
      z-index: 1;

      &.anniversary {
        color: #2dd4bf;
        background: rgba(45, 212, 191, 0.08);
        border: 1px solid rgba(45, 212, 191, 0.22);
      }

      &.bday {
        color: #b49af5;
        background: rgba(155, 122, 239, 0.08);
        border: 1px solid rgba(155, 122, 239, 0.22);
      }
    }
  }
`;

function ScrollableSection({ children }) {
  const trackRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild;
    const cardWidth = firstCard ? firstCard.offsetWidth + 12 : 220;
    el.scrollBy({ left: dir * cardWidth * 2, behavior: 'smooth' });
  };

  let fadeClass = '';
  if (showLeft && showRight) fadeClass = 'fade-both';
  else if (showRight) fadeClass = 'fade-right';
  else if (showLeft) fadeClass = 'fade-left';

  return (
    <div className="scroll-section">
      <button
        type="button"
        className={`scroll-arrow left${showLeft ? '' : ' hidden'}`}
        onClick={() => scroll(-1)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div
        className={`scroll-track${fadeClass ? ` ${fadeClass}` : ''}`}
        ref={trackRef}
        onScroll={updateArrows}
      >
        {children}
      </div>
      <button
        type="button"
        className={`scroll-arrow right${showRight ? '' : ' hidden'}`}
        onClick={() => scroll(1)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
    </div>
  );
}

ScrollableSection.propTypes = {
  children: PropTypes.node.isRequired,
};

const getCurrentMonthName = () => new Date().toLocaleString('default', { month: 'long' });

export const HomePreview = () => {
  const currentYear = new Date().getFullYear();
  const { data: holidaysData } = useQuery(CalendarEventListQuery, {
    variables: { year: currentYear, upcoming: true },
  });
  const { data: celebrationData } = useQuery(CelebrationQuery, {
    variables: { months: 3 },
  });
  const holidays = holidaysData?.staff_emp_calendar_event?.events || [];
  const allCelebrations = celebrationData?.staff_emp_celebration_celebration?.items || [];

  const now = new Date();
  const currentMonth = now.getMonth();
  const nextMonth = (currentMonth + 1) % 12;
  const celebrations = allCelebrations.filter((item) => {
    const d = new Date(item.date);
    const m = d.getMonth();
    return m === currentMonth || m === nextMonth;
  });

  return (
    <PreviewWrapper>
      {/* Greeting */}
      <div className="greeting">
        <div className="greeting-name">
          Good Afternoon, Steffi
        </div>
        <div className="greeting-sub">
          Tuesday, 17 Mar 2026 &middot; Roanuz
        </div>
      </div>

      {/* Leave Balance */}
      <div className="leave-balance-card">
        <div className="leave-balance-left">
          <div>
            <div className="leave-balance-row">
              <span className="leave-balance-number">19</span>
              <span className="leave-balance-sub">Leave Balance</span>
            </div>
          </div>
          <Link
            to={Pages.staff.leave.create.meta.url}
            className="request-leave-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Request Leave
          </Link>
        </div>
      </div>

      {/* Empty Leaves State */}
      <div className="leaves-section">
        <div className="section-header">
          <div className="section-title">
            My Recent Leaves
          </div>
          <span className="section-action">
            View all
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </span>
        </div>
        <div className="empty-state">
          <div className="empty-illustration">
            <svg
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Window frame */}
              <rect
                x="14"
                y="8"
                width="92"
                height="80"
                rx="10"
                fill="#16161a"
                stroke="#3a3a44"
                strokeWidth="4"
              />
              {/* Cross bars */}
              <line
                x1="60"
                y1="8"
                x2="60"
                y2="88"
                stroke="#3a3a44"
                strokeWidth="3.5"
              />
              <line
                x1="14"
                y1="48"
                x2="106"
                y2="48"
                stroke="#3a3a44"
                strokeWidth="3.5"
              />
              {/* Window sill */}
              <line
                x1="8"
                y1="90"
                x2="112"
                y2="90"
                stroke="#3a3a44"
                strokeWidth="3"
                strokeLinecap="round"
              />
              {/* Top-left pane: purple dot */}
              <circle
                cx="32"
                cy="32"
                r="5"
                fill="#9b7aef"
                opacity="0.7"
              />
              <circle
                cx="42"
                cy="22"
                r="1.5"
                fill="#6b697a"
                opacity="0.4"
              />
              {/* Top-right pane: green dot + purple dot */}
              <circle
                cx="76"
                cy="22"
                r="3"
                fill="#4ade80"
                opacity="0.5"
              />
              <circle
                cx="90"
                cy="34"
                r="3.5"
                fill="#9b7aef"
                opacity="0.4"
              />
              <circle
                cx="82"
                cy="40"
                r="1.5"
                fill="#6b697a"
                opacity="0.3"
              />
              {/* Bottom-left: teal dot */}
              <circle
                cx="28"
                cy="62"
                r="2.5"
                fill="#2dd4bf"
                opacity="0.5"
              />
              {/* Coffee mug body */}
              <rect
                x="40"
                y="80"
                width="24"
                height="18"
                rx="3"
                fill="#3a3a44"
              />
              {/* Mug handle */}
              <path
                d="M64 84 C70 84, 70 94, 64 94"
                stroke="#3a3a44"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />
              {/* Steam lines */}
              <path
                d="M47 78 Q49 72, 47 66"
                stroke="#9b7aef"
                strokeWidth="1.8"
                fill="none"
                opacity="0.6"
                strokeLinecap="round"
              />
              <path
                d="M52 77 Q54 70, 52 63"
                stroke="#9b7aef"
                strokeWidth="1.8"
                fill="none"
                opacity="0.45"
                strokeLinecap="round"
              />
              <path
                d="M57 78 Q59 73, 57 67"
                stroke="#9b7aef"
                strokeWidth="1.5"
                fill="none"
                opacity="0.3"
                strokeLinecap="round"
              />
              {/* Bottom-right: crescent moon */}
              <circle
                cx="84"
                cy="70"
                r="11"
                fill="#2dd4bf"
                opacity="0.5"
              />
              <circle
                cx="89"
                cy="65"
                r="10"
                fill="#16161a"
              />
              {/* Small purple dot near moon */}
              <circle
                cx="74"
                cy="78"
                r="1.5"
                fill="#9b7aef"
                opacity="0.3"
              />
            </svg>
          </div>
          <div className="empty-text">
            <div className="empty-title">
              No recent leaves
            </div>
            <div className="empty-desc">
              Enjoy the rhythm.
            </div>
          </div>
        </div>
      </div>

      {/* Holidays */}
      <div className="holidays-section">
        <div className="section-header">
          <div className="section-title">
            Upcoming Holidays
          </div>
          <span className="section-action">
            View all
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </span>
        </div>
        {holidays.length > 0 ? (
          <ScrollableSection>
            {holidays.map((event) => {
              const isOptional = event.event?.kind?.toLowerCase() === 'optionalholiday';
              return (
                <div
                  className={`holiday-card${isOptional ? ' optional' : ''}`}
                  key={event.resource.key}
                >
                  <div className="holiday-date-line">
                    {friendlyDate(event.event.date, null, 'MMM DD, ddd')}
                  </div>
                  <div className="holiday-name">{event.event.name}</div>
                  <div className="holiday-tag">
                    {isOptional ? 'Optional Holiday' : 'Public Holiday'}
                  </div>
                </div>
              );
            })}
          </ScrollableSection>
        ) : (
          <div className="empty-message">No upcoming holidays</div>
        )}
      </div>

      {/* Celebrations */}
      <div className="celebrations-section">
        <div className="section-header">
          <div className="section-title">
            {`Celebrations in ${getCurrentMonthName()}`}
          </div>
          <span className="section-action">
            View all
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </span>
        </div>
        {celebrations.length > 0 ? (
          <ScrollableSection>
            {celebrations.map((person) => (
              <div
                className={`celeb-card${person.kind === 'birthday' ? ' birthday' : ''}`}
                key={person.emp_id}
              >
                <div className={`celeb-icon ${person.kind === 'birthday' ? 'purple' : 'teal'}`}>
                  {person.kind === 'birthday' ? '\uD83C\uDF82' : '\uD83C\uDFC6'}
                </div>
                <div className="celeb-name">{person.name}</div>
                <div className="celeb-date">
                  {parseDate(person.date).format('MMM DD')}
                </div>
                <div className={`celeb-badge ${person.kind === 'birthday' ? 'bday' : 'anniversary'}`}>
                  {person.kind === 'birthday'
                    ? '\uD83C\uDF82 Birthday'
                    : `${person.years >= 5 ? '\uD83C\uDFC6' : '\u2B50'} ${person.years} years`}
                </div>
              </div>
            ))}
          </ScrollableSection>
        ) : (
          <div className="empty-message">No celebrations this month</div>
        )}
      </div>
    </PreviewWrapper>
  );
};

HomePreview.propTypes = {
};
